import { Alert, message } from "antd";
import axios from "axios";
import React, { Component } from "react";
import { connect } from "react-redux";
import moment from "moment";
import {
  Form,
  ModalItem,
  FormItem,
  ButtonItem,
  DatePickerItem,
  ErrorItem,
} from "../../../../Components/Items";

// Cadencias que el generador sabe construir (TipoPeriodo.diasPeriodo).
// Debe empatar con TIPO_POR_DIAS_PERIODO en Utils/generacionPeriodos.utils.js
const DIAS_PERIODO_CON_CADENCIA = {
  7: "semanal",
  14: "catorcenal",
  15: "quincenal",
  30: "mensual",
};

const DIAS_SEMANA = [
  "domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado",
];

class GenerarPeriodos extends Component {
  formRef = React.createRef();

  state = {
    loading: false,
    visible: this.props.visible,
  };

  componentDidUpdate(prevProps) {
    if (prevProps.visible !== this.props.visible) {
      this.setState({ visible: this.props.visible });
      if (this.props.visible) {
        this.formRef.current?.setFieldsValue({
          fechaInicio: this.ajustarAInicio(this.props.fechaInicioSugerida),
          fechaFin: this.ajustarAFin(this.props.fechaFinSugerida),
        });
      }
    }
  }

  // Ancla de la cadencia: la fecha con la que se configuró el tipo. Viene de la
  // base anclada al mediodía UTC (ver Utils/fechaDia.utils), así que al leerla
  // en local cae en el día correcto en cualquier zona de México.
  ancla() {
    return this.props.fechaInicioEjercicio
      ? moment(this.props.fechaInicioEjercicio).startOf("day")
      : null;
  }

  // ¿Esa fecha es un día en el que arranca un periodo de este tipo?
  // - semanal/catorcenal: cae en la rejilla de 7/14 días que sale del ancla, o
  //   sea el mismo día de la semana Y la misma paridad en catorcenal.
  // - quincenal: día 1 o 16, la cadencia va pegada al calendario.
  // - mensual: el día del mes con el que se configuró el tipo.
  esInicioValido = (fecha) => {
    if (!fecha) return false;
    const dias = this.props.diasPeriodo;
    const dia = fecha.clone().startOf("day");

    if (dias === 15) return dia.date() === 1 || dia.date() === 16;

    const ancla = this.ancla();
    if (!ancla) return false;

    if (dias === 30) return dia.date() === ancla.date();

    if (dias === 7 || dias === 14) {
      const diff = dia.diff(ancla, "days");
      return (((diff % dias) + dias) % dias) === 0;
    }

    return false;
  };

  // Un día de cierre es el día anterior a un día de arranque. Sirve para las
  // cuatro cadencias sin casos especiales: el 15 cierra porque el 16 abre, y el
  // último día del mes cierra porque el 1 abre.
  esFinValido = (fecha) => {
    if (!fecha) return false;
    return this.esInicioValido(fecha.clone().startOf("day").add(1, "day"));
  };

  // Recorre la fecha sugerida hasta el día válido más cercano hacia atrás (a lo
  // mucho un periodo), para que el modal abra con algo que sí se puede elegir.
  ajustar(fecha, esValida) {
    if (!fecha) return null;
    const dias = this.props.diasPeriodo || 31;
    let candidata = fecha.clone().startOf("day");
    for (let i = 0; i <= dias + 1; i++) {
      if (esValida(candidata)) return candidata;
      candidata = candidata.subtract(1, "day");
    }
    return null;
  }

  ajustarAInicio = (fecha) => this.ajustar(fecha, this.esInicioValido);

  ajustarAFin = (fecha) => this.ajustar(fecha, this.esFinValido);

  onFinishFailed(e) {
    ErrorItem(e);
  }

  onFinish(values) {
    if (!values?.fechaInicio || !values?.fechaFin) {
      return message.info("Proporcione la fecha inicial y la final");
    }
    // El DatePicker deja teclear la fecha además de elegirla del calendario, así
    // que la restricción se vuelve a revisar aquí.
    if (!this.esInicioValido(values.fechaInicio)) {
      return message.info(`La fecha inicial debe caer en un día de inicio de periodo (${this.textoInicio()})`);
    }
    if (!this.esFinValido(values.fechaFin)) {
      return message.info(`La fecha final debe caer en un día de cierre de periodo (${this.textoFin()})`);
    }
    if (values.fechaFin.isBefore(values.fechaInicio, "day")) {
      return message.info("La fecha final no puede ser anterior a la inicial");
    }

    this.setState({ loading: true });
    axios
      .post(
        "periodos/generarPeriodosTipoPeriodo",
        {
          empresaId: this.props.empresaId,
          tipoPeriodoId: this.props.tipoPeriodoId,
          fechaInicio: values.fechaInicio.format("YYYY-MM-DD"),
          fechaFin: values.fechaFin.format("YYYY-MM-DD"),
        },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        this.setState({ loading: false });
        if (res.data.success !== 2) {
          return message.error(
            res.data.message || "Sucedió un error y no se pudieron generar los periodos"
          );
        }

        const generados = res.data.generados || 0;
        const omitidos = res.data.omitidos || 0;
        if (generados === 0 && omitidos === 0) {
          message.info("No hubo periodos que generar en ese rango");
        } else if (generados === 0) {
          message.info(`Todos los periodos del rango (${omitidos}) ya existían`);
        } else {
          message.success(`${generados} periodo(s) generado(s)`);
        }
        if (generados > 0 && omitidos > 0) {
          message.warning(`${omitidos} periodo(s) del rango ya existían y se omitieron`);
        }
        this.cerrarModal();
      })
      .catch(() => {
        this.setState({ loading: false });
        message.error("Error al comunicarse con el servidor");
      });
  }

  // Cómo describirle al usuario qué días puede elegir
  textoInicio() {
    const dias = this.props.diasPeriodo;
    const ancla = this.ancla();
    if (dias === 15) return "día 1 o 16";
    if (dias === 30) return ancla ? `día ${ancla.date()} del mes` : "día del mes del tipo";
    if (!ancla) return "según la cadencia del tipo";
    const nombreDia = DIAS_SEMANA[ancla.day()];
    return dias === 14 ? `cada 14 días, en ${nombreDia}` : `cada ${nombreDia}`;
  }

  textoFin() {
    const dias = this.props.diasPeriodo;
    const ancla = this.ancla();
    if (dias === 15) return "día 15 o último del mes";
    if (dias === 30) {
      if (!ancla) return "día previo al del tipo";
      return ancla.date() === 1
        ? "último día del mes"
        : `día ${ancla.date() - 1} del mes`;
    }
    if (!ancla) return "según la cadencia del tipo";
    const nombreDia = DIAS_SEMANA[ancla.clone().subtract(1, "day").day()];
    return dias === 14 ? `cada 14 días, en ${nombreDia}` : `cada ${nombreDia}`;
  }

  cerrarModal = () => {
    this.setState({ visible: false });
    this.props.cerrarGenerarPeriodos();
    if (this.formRef.current) {
      this.formRef.current.resetFields();
    }
  };

  render() {
    const cadencia = DIAS_PERIODO_CON_CADENCIA[this.props.diasPeriodo];

    return (
      <ModalItem
        title={`Generar Periodos${this.props.nombreTipoPeriodo ? ` · ${this.props.nombreTipoPeriodo}` : ""}`}
        visible={this.state.visible}
        spinning={this.state.loading}
        onCancel={this.cerrarModal}
        width={650}
        forceRender
      >
        {cadencia ? (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message={`Cadencia ${cadencia}`}
            description={
              `El calendario solo deja elegir los días que respetan la rejilla del tipo: la fecha inicial en un día de arranque (${this.textoInicio()}) y la final en un día de cierre (${this.textoFin()}). ` +
              "Los periodos que ya existan en el rango se omiten."
            }
          />
        ) : (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="Este tipo de periodo no tiene cadencia"
            description="Solo se pueden generar en lote los tipos semanal, catorcenal, quincenal y mensual. Los periodos de este tipo se dan de alta uno por uno."
          />
        )}

        <Form
          ref={this.formRef}
          name="modalGenerarPeriodos"
          layout
          bottom={
            <ButtonItem
              type="primary"
              htmlType="submit"
              style={{ display: "block", margin: "0 0 0 auto" }}
              loading={this.state.loading}
              disabled={!cadencia}
            >
              Generar
            </ButtonItem>
          }
          onFinish={this.onFinish.bind(this)}
          onFinishFailed={this.onFinishFailed}
        >
          <FormItem name="fechaInicio" label="Fecha Inicial" required margin>
            <DatePickerItem
              placeholder="Desde qué periodo generar"
              style={{ width: "100%" }}
              disabled={!cadencia}
              disabledDate={cadencia ? (d) => !this.esInicioValido(d) : undefined}
            />
          </FormItem>

          <FormItem name="fechaFin" label="Fecha Final" required margin>
            <DatePickerItem
              placeholder="Hasta qué periodo generar"
              style={{ width: "100%" }}
              disabled={!cadencia}
              disabledDate={cadencia ? (d) => !this.esFinValido(d) : undefined}
            />
          </FormItem>
        </Form>
      </ModalItem>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
});

export default connect(mapStateToProps)(GenerarPeriodos);
